<template>
  <div>
    <v-simple-table dense>
      <template v-slot:default>
        <thead>
        <tr>
          <th class="text-left">Local de recebimento</th>
          <th class="text-left">Endereço</th>
          <th class="text-left">Janela de recebimento</th>
          <th class="text-left">Observações</th>
          <th class="text-left">Ações</th>
        </tr>
        </thead>

        <tbody>
        <tr v-for="item in locais" :key="item.id">
          <td>{{ getNomeFilial(item.filial) }}</td>
          <td>{{ item.endereco }}</td>
          <td>{{ formatJanelaRecebimento(item) }}</td>
          <td>{{ item.observacao || '-' }}</td>

          <td class="d-flex">
            <v-btn icon small @click="$emit('delete', item)">
              <v-icon>mdi-trash-can-outline</v-icon>
            </v-btn>

            <v-btn icon small @click="$emit('edit', item)">
              <v-icon>mdi-pencil-outline</v-icon>
            </v-btn>
          </td>
        </tr>
        </tbody>
      </template>
    </v-simple-table>

    <div v-if="!locais || locais.length === 0" class="empty-data">
      Sem locais de recebimento adicionado
    </div>

    <div class="mt-4">
      <v-btn
          class="wc-btn-actions"
          color="primary"
          text
          @click="$emit('add')"
      >
        Adicionar local de recebimento
        <v-icon right dark>mdi-plus</v-icon>
      </v-btn>
    </div>

    <div class="wc-prazo-dias-field mb-6">
      <p class="mb-3 font-weight-medium">Prazo de entrega desejado (dias) <span style="color: #fbbd5a">*</span></p>
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <div v-on="on" v-bind="attrs">
            <v-text-field
              class="wc-prazo-input"
              :value="prazoEntregaDesejadoDias"
              placeholder="30"
              outlined
              dense
              type="number"
              min="1"
              max="9999"
              :error-messages="prazoErrorMessagesList"
              hide-details="auto"
              @input="onPrazoInput"
            />
          </div>
        </template>
        <span>Meta interna de entrega para avaliar as propostas. O fornecedor não vê este valor. Na comparação, o sistema marca cada proposta como acima/abaixo do desejado.</span>
      </v-tooltip>
      <p class="wc-prazo-helper text-caption grey--text text--darken-1 mb-0 mt-1">
        Usado apenas para comparar propostas. Não é enviado ao fornecedor.
      </p>
    </div>

    <!-- Dialog de edição/criação -->
    <LocaisRecebimentoDialog
        :visible="dialogState.isOpen"
        :handleClose="handleCloseDialog"
        :editData="dialogState.itemEdit"
        @save="onSave"
    />
  </div>
</template>

<script setup>
import { computed } from "vue";
import LocaisRecebimentoDialog from "./LocaisRecebimentoDialog.vue";

const props = defineProps({
  locais: { type: Array, required: true },
  empresas: { type: Array, required: true },
  dialogState: { type: Object, required: true },
  handleCloseDialog: { type: Function, required: true },
  prazoEntregaDesejadoDias: { type: [Number, String], default: 30 },
  prazoErrorMessages: { type: [String, Array], default: () => [] },
});

const emit = defineEmits(['add', 'edit', 'delete', 'save', 'update:prazoEntregaDesejadoDias']);

const prazoErrorMessagesList = computed(() => {
  const e = props.prazoErrorMessages;
  if (e === null || e === undefined || e === "") return [];
  return Array.isArray(e) ? e : [e];
});

const onPrazoInput = (value) => {
  const raw =
    value && typeof value === "object" && "target" in value
      ? value.target.value
      : value;
  if (raw === "" || raw === null || typeof raw === "undefined") {
    emit("update:prazoEntregaDesejadoDias", "");
    return;
  }
  const n = parseInt(String(raw), 10);
  emit("update:prazoEntregaDesejadoDias", Number.isNaN(n) ? "" : n);
};

const getNomeFilial = (idfilial) => {
  const filial = props.empresas.find(e => e.idfilial === idfilial);
  return filial ? (filial.razao_social || filial.nome_fantasia) : '';
};

const formatJanelaRecebimento = (item) => {
  const horario = `${item.inicio} - ${item.fim}`;

  if (!item.dias || item.dias.length === 0) {
    return horario;
  }

  // Mapeamento dos dias da semana para abreviações
  const diasAbrev = {
    'segunda': 'Seg',
    'terca': 'Ter',
    'quarta': 'Qua',
    'quinta': 'Qui',
    'sexta': 'Sex',
    'sabado': 'Sáb',
    'domingo': 'Dom'
  };

  const dias = Array.isArray(item.dias) ? item.dias : [];
  const diasFormatados = dias
    .map((dia) => {
      if (typeof dia === "string") {
        return diasAbrev[dia.toLowerCase()] || dia;
      }
      if (dia && typeof dia === "object" && dia.nome) {
        const n = String(dia.nome).toLowerCase();
        return diasAbrev[n] || dia.nome;
      }
      return String(dia);
    })
    .join(", ");

  return `${diasFormatados} ${horario}`;
};

const onSave = (data) => {
  emit('save', data);
};
</script>

<style scoped>
.wc-prazo-dias-field {
  max-width: 224px; /* ~70% de 320px */
  margin-top: 2.25rem;
}

.wc-prazo-helper {
  line-height: 1.35;
  white-space: nowrap;
}

/* Remove setas do input number (Chrome, Safari, Edge) */
.wc-prazo-dias-field ::v-deep input[type="number"]::-webkit-outer-spin-button,
.wc-prazo-dias-field ::v-deep input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox e compatibilidade */
.wc-prazo-dias-field ::v-deep input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}

.empty-data {
  width: 100%;
  height: 30px;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}
</style>
